import ClienteInfoCard from "./ClienteInfoCard";
import { useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { APIURL } from '../../../configApi/apiConfig';
import { useAuth } from '../../AuthContext/AuthContext';
import axios from "../../../configApi/axiosConfig";
import { useNavigate } from "react-router-dom";
import { fetchConsultaYNotifica, fechaHoraEcuador } from "../../Utils";
import ModalConfirmacionRechazo from '../../SolicitudGrande/Cabecera/ModalConfirmacionRechazo'; // Ajusta la ruta si es necesario
import { GoogleMapModal } from "../../ListadoSolicitud/DomicilioModal"
import jsPDF from "jspdf";
import uploadFile from "../../../hooks/uploadFile";
import { RotateCcw, XCircle, CheckCircle2, AlertTriangle } from "lucide-react";

// Tipos de documento "Respaldo N" habilitados para carga durante un reproceso de respaldos pendientes
const RESPALDO_TIPOS = {
    "Respaldo 1": 16,
    "Respaldo 2": 17,
    "Respaldo 3": 18,
    "Respaldo 4": 19,
    "Respaldo 5": 20,
    "Respaldo 6": 21,
    "Respaldo 7": 22,
    "Respaldo 8": 23,
    "Respaldo 9": 24,
    "Respaldo 10": 25,
};

// Estados de RespaldosPendientesWeb: la aprobación/negación de los respaldos se controla
// únicamente desde esta tabla, nunca por documento individual.
const RESPALDOS_PENDIENTE_CARGA = 1;
const RESPALDOS_ENVIADO_A_REVISAR = 2;
const RESPALDOS_APROBADO = 3;
const RESPALDOS_VOLVER_A_CORREGIR = 4; // no termina el ciclo: reabre uno nuevo para que el vendedor recargue
const RESPALDOS_RECHAZADO = 5; // terminal: no se vuelve a habilitar la carga

// Configuración visual + textos de confirmación de las 3 acciones que el analista puede tomar
// sobre un ciclo "Enviado a revisar" de RespaldosPendientesWeb.
const RESPALDOS_ACCIONES = {
    [RESPALDOS_VOLVER_A_CORREGIR]: {
        label: "Volver a corregir",
        Icon: RotateCcw,
        buttonClasses: "bg-orange-500 hover:bg-orange-600 focus-visible:ring-orange-400",
        confirmTitle: "¿Volver a corregir los respaldos?",
        confirmText: "El vendedor deberá volver a cargar los respaldos y enviarlos a revisión nuevamente. Una vez que confirmes, no vas a poder deshacer esta acción.",
        confirmClasses: "bg-orange-500 hover:bg-orange-600",
        confirmCheckLabel: "Entiendo que el vendedor deberá recargar los respaldos y deseo continuar.",
    },
    [RESPALDOS_RECHAZADO]: {
        label: "Rechazar",
        Icon: XCircle,
        buttonClasses: "bg-red-600 hover:bg-red-700 focus-visible:ring-red-400",
        confirmTitle: "¿Rechazar los respaldos?",
        confirmText: "Esta acción es definitiva: no se volverá a habilitar la carga de respaldos para esta solicitud y no vas a poder deshacerla.",
        confirmClasses: "bg-red-600 hover:bg-red-700",
        confirmCheckLabel: "Entiendo que esta acción es definitiva y no se puede deshacer, y deseo continuar.",
    },
    [RESPALDOS_APROBADO]: {
        label: "Aprobar",
        Icon: CheckCircle2,
        buttonClasses: "bg-green-600 hover:bg-green-700 focus-visible:ring-green-400",
        confirmTitle: "¿Aprobar los respaldos?",
        confirmText: "La solicitud quedará con los respaldos aprobados de forma definitiva. Una vez que confirmes, no vas a poder deshacer esta acción.",
        confirmClasses: "bg-green-600 hover:bg-green-700",
        confirmCheckLabel: "Entiendo que esta acción es definitiva y no se puede deshacer, y deseo continuar.",
    },
};

export function GestorDocumentos({
    id,
    NumeroSolicitud,
    nombre,
    cedula,
    fecha,
    almacen,
    foto,
    vendedor,
    consulta,
    estadoVerifD,
}) {
    const { userData } = useAuth();
    const { state } = useLocation();
    const permisos = state?.permisos || [];
    const [files, setFiles] = useState({});
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const [view, setView] = useState(false);
    const modalRef = useRef(null);
    const confirmModalRef = useRef(null);
    const [filePreviews, setFilePreviews] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false); // Estados para el modal de confirmación
    const [confirmAction, setConfirmAction] = useState(null); // 'aprobar' o 'rechazar'
    const [currentDocId, setCurrentDocId] = useState(null);  // ID del documento
    const [observacion, setObservacion] = useState("");
    const [observaciones, setObservaciones] = useState({}); // Para almacenar las observaciones por documento
    const [allDocuments, setAllDocuments] = useState([]); // Estado para mantener todos los documentos de la solicitud
    const [documentStatus, setDocumentStatus] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);  // Estado para el carrusel
    const [flatFiles, setFlatFiles] = useState([]);  // Array de los archivos para el carrusel
    const [showApproveAllButton, setShowApproveAllButton] = useState(false); // Mostrar botón de aprobar 
    const [showRevisionButton, setShowRevisionButton] = useState(false); // Mostrar botón de revisión
    // estados para el modal global
    const [showGlobalConfirmModal, setShowGlobalConfirmModal] = useState(false);
    const [globalConfirmAction, setGlobalConfirmAction] = useState(null);
    const globalConfirmModalRef = useRef(null);
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

    // estados para el sub-flujo "Aprobar Todos los Documentos": todo correcto vs faltan respaldos
    const [aprobarStep, setAprobarStep] = useState('choice'); // 'choice' | 'faltanRespaldos'
    const [cantidadRespaldosFaltantes, setCantidadRespaldosFaltantes] = useState(1);
    const [detalleRespaldosFaltantes, setDetalleRespaldosFaltantes] = useState('');
    const [enviandoAprobacion, setEnviandoAprobacion] = useState(false);

    // registro activo (Estado=1) de respaldos pendientes de carga para esta solicitud, si existe
    const [respaldosPendientesActivo, setRespaldosPendientesActivo] = useState(null);
    const [resolviendoRespaldos, setResolviendoRespaldos] = useState(false);
    // Estado (Aprobar/Rechazar/Volver a corregir) pendiente de confirmar, o null si el modal está cerrado
    const [confirmarAccionRespaldos, setConfirmarAccionRespaldos] = useState(null);
    // Doble confirmación: el botón "Sí, confirmar" solo se habilita si el usuario marca este check
    const [dobleConfirmacionRespaldos, setDobleConfirmacionRespaldos] = useState(false);

    // carga del respaldo faltante (vendedor/jefe de almacén) mientras hay un pendiente activo
    // { "Respaldo 1": File, ... } y qué tipo se está subiendo en este momento (para deshabilitar solo ese renglón)
    const [respaldoArchivos, setRespaldoArchivos] = useState({});
    const [subiendoRespaldoTipo, setSubiendoRespaldoTipo] = useState(null);

    const [clientInfo, setClientInfo] = useState({
        id: "",
        nombre: "",
        cedula: "",
        fecha: "",
        almacen: "",
        foto: "",
        NumeroSolicitud: "",
        vendedor: "",
        consulta: "",
        estadoVerifD: "",
    });

    const puedeVisualizarfirma = () => {
        const permiso = permisos.find((p) => p.Permisos === "VISUALIZAR FIRMA");
        return permiso && permiso.Activo;
    };
    // Controla quién puede decidir Aprobado / Volver a corregir / Rechazado sobre RespaldosPendientesWeb
    const puedeAprobarRespaldos = () => {
        const permiso = permisos.find((p) => p.Permisos === "APROBAR SOLICITUD DE RESPALDO");
        return permiso && permiso.Activo;
    };
    // mostrar botones
    useEffect(() => {
        if (allDocuments.length > 0) {
            const filteredDocs = allDocuments.filter(doc => doc.type >= 1 && doc.type <= 15 || doc.type > 25);
            if (filteredDocs.length > 0) {
                const allReviewed = filteredDocs.every(doc => doc.estado === 3 || doc.estado === 4);
                if (allReviewed) {
                    // si todos los documentos estan aprobados
                    const allApproved = filteredDocs.every(doc => doc.estado === 3);
                    setShowApproveAllButton(allApproved);
                    // si algún documento está en corrección
                    const hasRejectedDocs = filteredDocs.some(doc => doc.estado === 4);
                    setShowRevisionButton(hasRejectedDocs);
                } else {
                    setShowApproveAllButton(false);
                    setShowRevisionButton(false);
                }

            }
        }
    }, [allDocuments]);


    ///funcion para el croquis 

    const fetchCroquisCoords = async (tipo) => {
        try {
            // tipo: 1 = domicilio, 2 = laboral
            const response = await axios.get(APIURL.getCoordenadasId(clientInfo.id, tipo));
            if (response.data && response.data[0]) {
                const lat = response.data[0].latitud;
                const lng = response.data[0].longitud;
                setMapLat(lat);
                setMapLng(lng);
                setMapTitle(tipo === 1 ? "Croquis Domicilio" : "Croquis Laboral");
                setShowMapModal(true);
            } else {
                enqueueSnackbar("No hay coordenadas registradas para este croquis.", { variant: "warning" });
            }
        } catch (error) {
            enqueueSnackbar("Error al obtener coordenadas", { variant: "error" });
        }
    };

    const [showMapModal, setShowMapModal] = useState(false);
    const [mapLat, setMapLat] = useState(null);
    const [mapLng, setMapLng] = useState(null);
    const [mapTitle, setMapTitle] = useState(""); // Para saber cuál croquis se está mostrando
    // Función para obtener todos los documentos
    const fetchAllDocuments = async () => {
        try {
            if (!clientInfo.id) return;

            // Obtenemos documentos con estado 2 (pendientes)
            const pendingUrl = APIURL.get_documentosEstado(clientInfo.id, 2);
            let pendingDocs = [];
            try {
                const pendingResponse = await axios.get(pendingUrl);
                if (pendingResponse.status === 200 && Array.isArray(pendingResponse.data)) {
                    pendingDocs = pendingResponse.data;
                }
            } catch (error) {
                console.log("No hay documentos pendientes o error al obtenerlos");
            }

            // Obtenemos documentos con estado 3 (aprobados)
            const approvedUrl = APIURL.get_documentosEstado(clientInfo.id, 3);
            let approvedDocs = [];
            try {
                const approvedResponse = await axios.get(approvedUrl);
                if (approvedResponse.status === 200 && Array.isArray(approvedResponse.data)) {
                    approvedDocs = approvedResponse.data;
                }
            } catch (error) {
                console.log("No hay documentos aprobados o error al obtenerlos");
            }

            // Obtenemos documentos con estado 4 (rechazados)
            const rejectedUrl = APIURL.get_documentosEstado(clientInfo.id, 4);
            let rejectedDocs = [];
            try {
                const rejectedResponse = await axios.get(rejectedUrl);
                if (rejectedResponse.status === 200 && Array.isArray(rejectedResponse.data)) {
                    rejectedDocs = rejectedResponse.data;
                }
            } catch (error) {
                console.log("No hay documentos rechazados o error al obtenerlos");
            }

            // Combinamos todos los documentos
            const allDocs = [
                ...pendingDocs,
                ...approvedDocs,
                ...rejectedDocs
            ];

            // Procesamos la información de todos los documentos 
            const processedDocs = [];
            const docStatus = {};

            allDocs.forEach(doc => {
                const fileName = doc.RutaDocumento.split('/').pop();
                const docType = getTipoDocumento(doc.idTipoDocumentoWEB);

                processedDocs.push({
                    id: doc.idDocumentosSolicitudWeb,
                    name: fileName,
                    url: doc.RutaDocumento,
                    type: doc.idTipoDocumentoWEB,
                    typeName: docType,
                    estado: doc.idEstadoDocumento
                });

                // Guardamos el estado del documento
                docStatus[doc.idDocumentosSolicitudWeb] = doc.idEstadoDocumento;
            });

            setAllDocuments(processedDocs);
            setDocumentStatus(docStatus);

            // todos los documentos para el carrusel
            const uploadedFiles = {};
            const previews = {};
            const allFiles = [];

            // Procesar todos los documentos para la interfaz
            allDocs.forEach((file) => {
                const sectionName = getTipoDocumento(file.idTipoDocumentoWEB);
                if (!uploadedFiles[sectionName]) {
                    uploadedFiles[sectionName] = [];
                    previews[sectionName] = [];
                }

                // Extrae el nombre del archivo desde la ruta
                const fileName = file.RutaDocumento.split('/').pop();
                const fileUrl = file.RutaDocumento;
                const fileObj = {
                    idDocumentosSolicitudWeb: file.idDocumentosSolicitudWeb,
                    name: fileName,
                    url: fileUrl,
                    type: 'application/pdf',
                    idTipoDocumento: file.idTipoDocumentoWEB,
                    sectionName: sectionName,
                    estado: file.idEstadoDocumento
                };

                uploadedFiles[sectionName].push(fileObj);
                previews[sectionName].push(fileUrl);

                // Agregar al array plano para el carrusel
                allFiles.push(fileObj);
            });

            setFiles(uploadedFiles);
            setFilePreviews(previews);
            setFlatFiles(allFiles);

            // Reiniciar el índice del carrusel cuando cambian los archivos
            setCurrentIndex(0);
        } catch (error) {
            enqueueSnackbar("Error al obtener archivos.", { variant: "error" });
            console.error("Error al obtener archivos:", error);
        }
    };

    useEffect(() => {
        if (clientInfo.id) {
            fetchAllDocuments();
            fetchRespaldosPendientesActivo();

            // Intentar cargar estados guardados de localStorage si existen
            const savedDocStatus = localStorage.getItem(`docStatus_${clientInfo.id}`);
            if (savedDocStatus) {
                setDocumentStatus(JSON.parse(savedDocStatus));
            }

            // Cargar observaciones guardadas
            const savedObservaciones = localStorage.getItem(`observaciones_${clientInfo.id}`);
            if (savedObservaciones) {
                setObservaciones(JSON.parse(savedObservaciones));
            }
        }
    }, [clientInfo.id]);

    // Consulta si la solicitud tiene un pedido de respaldos pendiente de carga (Estado=1)
    const fetchRespaldosPendientesActivo = async () => {
        try {
            const response = await axios.get(APIURL.get_respaldosPendientesActivo(clientInfo.id));
            setRespaldosPendientesActivo(response.data || null);
        } catch (error) {
            setRespaldosPendientesActivo(null);
        }
    };

    // Crea el registro de respaldos pendientes cuando el analista aprueba dejando constancia de que faltan respaldos
    const crearRespaldosPendientes = async (cantidad, detalle) => {
        const token = localStorage.getItem("token");
        const datos = {
            idCre_SolicitudWeb: clientInfo.id,
            CantidadRespaldos: Number(cantidad),
            Detalle: detalle,
            Usuario: userData.Nombre,
        };
        return axios.post(APIURL.post_respaldosPendientes(), datos, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
    };

    // El vendedor/jefe de almacén marca que ya cargó los respaldos solicitados y quedan listos para revisión
    const handleEnviarARevisar = async () => {
        if (!respaldosPendientesActivo) return;
        setResolviendoRespaldos(true);
        try {
            const token = localStorage.getItem("token");
            await axios.patch(
                APIURL.patch_respaldosPendientesEnviarARevisar(respaldosPendientesActivo.idRespaldosPendientesWeb),
                { Usuario: userData.Nombre },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            enqueueSnackbar("Respaldos enviados a revisión", { variant: "success" });
            await fetchRespaldosPendientesActivo();
        } catch (error) {
            enqueueSnackbar("Error al enviar los respaldos a revisión", { variant: "error" });
        } finally {
            setResolviendoRespaldos(false);
        }
    };

    // El analista aprueba o niega los respaldos que el vendedor ya cargó y envió a revisión.
    // Esta es la ÚNICA forma de aprobar/negar respaldos: nunca por documento individual.
    const handleResolverRespaldos = async (estadoResolucion) => {
        if (!respaldosPendientesActivo) return;
        setResolviendoRespaldos(true);
        try {
            const token = localStorage.getItem("token");
            await axios.patch(
                APIURL.patch_respaldosPendientesResolver(respaldosPendientesActivo.idRespaldosPendientesWeb),
                { Estado: estadoResolucion, UsuarioResolucion: userData.Nombre },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const textos = {
                [RESPALDOS_APROBADO]: {
                    snackbar: "Respaldos aprobados correctamente",
                    title: "¡Respaldos aprobados! 🎉",
                    body: `¡Hola! Los respaldos cargados para la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} fueron aprobados. ¡Gracias!
					Fecha: ${fechaHoraEcuador}`,
                },
                [RESPALDOS_VOLVER_A_CORREGIR]: {
                    snackbar: "Se envió a corrección: el vendedor debe volver a cargar",
                    title: "Respaldos: volver a corregir 🔄",
                    body: `¡Hola! Los respaldos cargados para la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} no fueron aceptados. Por favor vuelve a cargarlos y envíalos a revisión nuevamente. ¡Gracias!
					Fecha: ${fechaHoraEcuador}`,
                },
                [RESPALDOS_RECHAZADO]: {
                    snackbar: "Respaldos rechazados",
                    title: "Respaldos rechazados ❌",
                    body: `¡Hola! Los respaldos cargados para la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} fueron rechazados. ¡Gracias!
					Fecha: ${fechaHoraEcuador}`,
                },
            };
            const texto = textos[estadoResolucion];

            enqueueSnackbar(texto.snackbar, { variant: "success" });

            fetchConsultaYNotifica(clientInfo.id, clientInfo, {
                title: texto.title,
                body: texto.body,
                type: "success",
                empresa: "CREDI",
                url: "",
                tipo: "vendedor",
            });

            await fetchRespaldosPendientesActivo();
        } catch (error) {
            enqueueSnackbar("Error al resolver los respaldos pendientes", { variant: "error" });
        } finally {
            setResolviendoRespaldos(false);
        }
    };

    // Sube el respaldo de un tipo puntual (ej. "Respaldo 3") desde la lista lateral, mientras hay un pendiente activo
    const handleSubirRespaldo = async (tipo) => {
        const archivo = respaldoArchivos[tipo];
        if (!archivo) {
            enqueueSnackbar("Selecciona un archivo antes de subir", { variant: "error" });
            return;
        }

        setSubiendoRespaldoTipo(tipo);
        try {
            const resultado = await uploadFile(
                archivo,
                clientInfo.almacen,
                clientInfo.cedula,
                clientInfo.NumeroSolicitud,
                tipo
            );

            if (!resultado?.url) {
                throw new Error("No se recibió la URL del archivo subido");
            }

            const token = localStorage.getItem("token");
            await axios.post(
                APIURL.post_documentos(),
                {
                    idCre_SolicitudWeb: clientInfo.id,
                    idTipoDocumentoWEB: RESPALDO_TIPOS[tipo],
                    RutaDocumento: resultado.url,
                    Observacion: "",
                    Usuario: userData.Nombre,
                    IdUsuario: userData.idUsuario,
                    // Esta pantalla solo consulta documentos en estado 2/3/4; sin esto el nuevo
                    // archivo caería en el default de BD (1) y quedaría invisible aquí.
                    idEstadoDocumento: 2,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            enqueueSnackbar(`${tipo} subido correctamente`, { variant: "success" });
            setRespaldoArchivos((prev) => {
                const next = { ...prev };
                delete next[tipo];
                return next;
            });
            await fetchAllDocuments();
        } catch (error) {
            enqueueSnackbar("Error al subir el respaldo", { variant: "error" });
        } finally {
            setSubiendoRespaldoTipo(null);
        }
    };

    // API cambiar estado del documento
    const estadoDocumentos = async (idDocumentosSolicitudWeb, idEstadoDocumento) => {
        try {
            const token = localStorage.getItem("token");
            const url = APIURL.patch_documentos(idDocumentosSolicitudWeb);
            const datos = {
                idEstadoDocumento: idEstadoDocumento,
            };

            const response = await axios.patch(url, datos, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                // Guardamos la observación
                const updatedObservaciones = { ...observaciones };
                updatedObservaciones[idDocumentosSolicitudWeb] = observacion;
                setObservaciones(updatedObservaciones);

                // Guardamos en localStorage
                localStorage.setItem(`observaciones_${clientInfo.id}`, JSON.stringify(updatedObservaciones));

                // Mensaje según la acción
                const accion = idEstadoDocumento === 3 ? "aprobado" : "rechazado";
                enqueueSnackbar(`Documento ${accion} correctamente`, { variant: "success" });

                // Actualizamos el estado del documento
                const updatedStatus = { ...documentStatus };
                updatedStatus[idDocumentosSolicitudWeb] = idEstadoDocumento;
                setDocumentStatus(updatedStatus);

                // Guardamos en localStorage
                localStorage.setItem(`docStatus_${clientInfo.id}`, JSON.stringify(updatedStatus));

                // Actualizamos la lista de archivos pendientes
                await fetchAllDocuments();
            } else {
                enqueueSnackbar("Error al enviar los datos", { variant: "error" });
            }
        } catch (error) {
            console.error("Error al enviar los datos:", error.response?.data);
            enqueueSnackbar("Error al enviar los datos: " + error.response?.data?.message || error.message, { variant: "error" });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('es-EC', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    //api actualizar estado de solicitudWeb
    const updateEstadoVerificacion = async (idEstadoVerificacionDocumental) => {
        try {
            const url = APIURL.update_soliciutd_telefonica(clientInfo.id, idEstadoVerificacionDocumental);

            const response = await axios.patch(url);

            if (response.status === 200) {
                // Mensaje de éxito con el estado actualizado
                const url_estado = APIURL.post_createtiemposolicitudeswebDto();
                await axios.post(url_estado, {

                    idCre_SolicitudWeb: clientInfo.id,
                    Tipo: 3,
                    idEstadoVerificacionDocumental: idEstadoVerificacionDocumental,
                    Usuario: userData.Nombre,
                });

                const estadoTexto = {
                    3: "Enviado para corrección",
                    4: "Aprobados",
                    5: "Rechazados"
                }[idEstadoVerificacionDocumental] || "Actualizado";

                enqueueSnackbar(`Documentos ${estadoTexto}`, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar("Error al actualizar la solicitud.", {
                    variant: "error",
                });
            }
        } catch (error) {
            enqueueSnackbar("Error al actualizar la solicitud.", {
                variant: "error",
            });
            console.error("Error al actualizar la solicitud:", error);
        }
    };
    ///para el rechazo 
    const updateEstadoVerificacionrechazo = async (idEstadoVerificacionDocumental, observacionModal) => {

        try {
            const url = APIURL.update_soliciutd_telefonica(clientInfo.id, idEstadoVerificacionDocumental);

            const response = await axios.patch(url);

            if (response.status === 200) {
                // Mensaje de éxito con el estado actualizado
                const url_estado = APIURL.post_createtiemposolicitudeswebDto();
                await axios.post(url_estado, {

                    idCre_SolicitudWeb: clientInfo.id,
                    Tipo: 3,
                    idEstadoVerificacionDocumental: idEstadoVerificacionDocumental,
                    Usuario: userData.Nombre,
                    Telefono: observacionModal
                });

                const estadoTexto = {
                    3: "Enviado para corrección",
                    4: "Aprobados",
                    5: "Rechazados"
                }[idEstadoVerificacionDocumental] || "Actualizado";

                enqueueSnackbar(`Documentos ${estadoTexto}`, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar("Error al actualizar la solicitud.", {
                    variant: "error",
                });
            }
        } catch (error) {
            enqueueSnackbar("Error al actualizar la solicitud.", {
                variant: "error",
            });
            console.error("Error al actualizar la solicitud:", error);
        }
    };

    //api enviar datos modal    
    const enviarObservacion = async (datos) => {
        try {
            const token = localStorage.getItem("token");
            const url = APIURL.post_observaciones();

            const response = await axios.post(url, datos, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status == 201) {
                enqueueSnackbar("Datos enviados correctamente", { variant: "success" });
            } else {
                enqueueSnackbar("Error al enviar los datos 1", { variant: "error" });
            }
        } catch (error) {
            console.error("Error al enviar los datos 2:", error.response?.data);
            enqueueSnackbar("Error al enviar los datos: " + error.response?.data?.message || error.message, { variant: "error" });
        }
    };

    const handleEnviarObservacion = () => {
        //objeto que se enviara a la api  
        const datosObserv = {
            idCre_SolicitudWeb: clientInfo.id,
            idDocumentosSolicitudWeb: currentDocId.idDocumentosSolicitudWeb,
            idUsuario: null,
            Observacion: String(observacion),
            TipoUsuario: 1,
            Usuario: userData.Nombre,
            idTipoDocumentoWEB: currentDocId.idTipoDocumento,
            Fecha: new Date(),
        };
        enviarObservacion(datosObserv);
    };

    // Función para mostrar el modal de confirmación
    const openConfirmModal = (file, action) => {
        setCurrentDocId(file);
        setConfirmAction(action);
        setObservacion(""); // Limpiar la observación anterior
        setShowConfirmModal(true);
    };

    // Función para confirmar la acción
    const handleConfirmAction = () => {
        // Validar si es necesaria la observación
        if (confirmAction === 'rechazar' && !observacion.trim()) {
            enqueueSnackbar("Por favor ingrese una observación", { variant: "error" });
            return;
        }

        // Ejecutar la acción correspondiente
        if (confirmAction === 'aprobar') {
            estadoDocumentos(currentDocId.idDocumentosSolicitudWeb, 3);
            if (observacion.trim()) {
                handleEnviarObservacion();
            }
        } else if (confirmAction === 'rechazar') {
            if (observacion.length < 1) {
                enqueueSnackbar("La observación debe tener al menos 10 caracteres", { variant: "error" });
                return;
            }
            estadoDocumentos(currentDocId.idDocumentosSolicitudWeb, 4);
            handleEnviarObservacion();
        }

        // Cerrar el modal
        setShowConfirmModal(false);
    };

    // Función para cancelar la acción
    const handleCancelAction = () => {
        setShowConfirmModal(false);
        setObservacion("");
    };

    // Funciones actualizadas para abrir el modal antes de ejecutar la acción
    const handleRechazar = (file) => {
        openConfirmModal(file, 'rechazar');
    };

    const handleAprobar = (file) => {
        openConfirmModal(file, 'aprobar');
    };

    const getTipoDocumento = (id) => {
        const documentoIds = {
            //   1: "Buro Credito",
            2: "Copia De Cedula",
            3: "Contrato de Compra",
            4: "Declaracion",
            5: "Pagare a la Orden",
            6: "Tabla de amortizacion",
            7: "Gastos de cobranza",
            8: "Compromiso Lugar de pago",
            9: "Acta",
            10: "Consentimiento",
            11: "Autorización",
            12: "Foto del Cliente",  //Servicio Basico
            13: "Foto Casa",  //Foto del Cliente
            14: "Servicio Basico",  //Croquis
            15: "Foto del Cliente Firmando",
            16: "Respaldo 1",
            17: "Respaldo 2",
            18: "Respaldo 3",
            19: "Respaldo 4",
            20: "Respaldo 5",
            21: "Respaldo 6",
            22: "Respaldo 7",
            23: "Respaldo 8",
            24: "Respaldo 9",
            25: "Respaldo 10",
            26: "Foto Trabajo",
            27: "PAPELETA DE VOTACIÓN - MOTOS"
        };
        return documentoIds[id] || `Documento Tipo ${id}`;
    };

    // Obtener el color según estado del documento
    const getStatusColor = (docId) => {
        const status = documentStatus[docId];
        if (status === 3) return "text-green-500"; // Aprobado
        if (status === 4) return "text-red-500";   // Rechazado
        return "text-gray-300"; // Pendiente
    };

    // Obtener el estado en texto
    const getStatusText = (docId) => {
        const status = documentStatus[docId];
        if (status === 3) return "✓ Aprobado";
        if (status === 4) return "✗ Rechazado";
        return "• Pendiente";
    };

    // título según el estado del documento actual
    const getCurrentDocumentStatusTitle = () => {
        if (flatFiles.length === 0) return "Documentos Subidos";

        const currentDocument = flatFiles[currentIndex];
        switch (currentDocument.estado) {
            case 3:
                return "Documento Aprobado";
            case 4:
                return "Documento Rechazado";
            default:
                return "Documento Pendiente";
        }
    };

    //color según el estado del documento actual
    const getCurrentDocumentStatusClass = () => {
        if (flatFiles.length === 0) return "text-gray-800";

        const currentDocument = flatFiles[currentIndex];
        switch (currentDocument.estado) {
            case 3:
                return "text-green-600";
            case 4:
                return "text-red-600";
            default:
                return "text-gray-800";
        }
    };

    useEffect(() => {
        if (location.state) {
            localStorage.setItem("clientInfo", JSON.stringify(location.state));
            setClientInfo(location.state);
        } else {
            const savedClientInfo = localStorage.getItem("clientInfo");
            if (savedClientInfo) {
                setClientInfo(JSON.parse(savedClientInfo));
            }
        }
    }, [location.state]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setView(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Efecto para detectar clicks fuera del modal de confirmación
    useEffect(() => {
        const handleClickOutsideConfirm = (event) => {
            if (confirmModalRef.current && !confirmModalRef.current.contains(event.target)) {
                setShowConfirmModal(false);
            }
        };

        if (showConfirmModal) {
            document.addEventListener("mousedown", handleClickOutsideConfirm);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideConfirm);
        };
    }, [showConfirmModal]);

    // Agrupar documentos por tipo
    const documentsByType = allDocuments.reduce((acc, doc) => {
        if (!acc[doc.typeName]) {
            acc[doc.typeName] = [];
        }
        acc[doc.typeName].push(doc);
        return acc;
    }, {});

    // Funciones para el carrusel
    const nextSlide = () => {
        if (flatFiles.length === 0) return;
        setCurrentIndex((prevIndex) => (prevIndex + 1) % flatFiles.length);
    };

    const prevSlide = () => {
        if (flatFiles.length === 0) return;
        setCurrentIndex((prevIndex) => (prevIndex - 1 + flatFiles.length) % flatFiles.length);
    };

    // botones de acción según el estado del documento
    const renderActionButtons = (document) => {
        //habilitar botones cuando estadoVerifD = 2
        if (clientInfo.estadoVerifD === 2) {
            if (document.idTipoDocumento >= 1 && document.idTipoDocumento <= 15 || document.idTipoDocumento > 25) {
                switch (document.estado) {
                    case 2: // Pendiente - mostrar ambos botones
                        return (
                            <>
                                <button
                                    type="button"
                                    name="rechazar"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleRechazar(document)}
                                >
                                    ❌
                                </button>
                                <button
                                    type="button"
                                    name="aprobar"
                                    className="text-green-500 hover:text-green-700"
                                    onClick={() => handleAprobar(document)}
                                >
                                    ✅
                                </button>
                            </>
                        );
                    case 3: // Aprobado - mostrar solo rechazar
                        return (
                            <button
                                type="button"
                                name="rechazar"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleRechazar(document)}
                            >
                                ❌
                            </button>
                        );
                    case 4: // Rechazado - mostrar solo aprobar
                        return (
                            <button
                                type="button"
                                name="aprobar"
                                className="text-green-500 hover:text-green-700"
                                onClick={() => handleAprobar(document)}
                            >
                                ✅
                            </button>
                        );
                    default:
                        return null;
                }
            }
        } else {
            return null;
        }
    };

    // detectar clicks fuera del modal global
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (globalConfirmModalRef.current && !globalConfirmModalRef.current.contains(event.target)) {
                setShowGlobalConfirmModal(false);
            }
        };
        if (showGlobalConfirmModal) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showGlobalConfirmModal]);

    // abrir modal global
    const openGlobalConfirmModal = (action) => {
        setGlobalConfirmAction(action);
        setAprobarStep('choice');
        setCantidadRespaldosFaltantes(1);
        setDetalleRespaldosFaltantes('');
        setShowGlobalConfirmModal(true);
    };

    // Aprobación con todos los documentos correctos (comportamiento original, sin respaldos pendientes)
    const handleAprobarTodoCorrecto = async () => {
        setEnviandoAprobacion(true);
        try {
            await updateEstadoVerificacion(4);
            if (Number(clientInfo?.TipoFirmaElectronica) === 2) {
                await axios.post(APIURL.autoAprobarBancoSolidario(clientInfo.id));
            }
            fetchConsultaYNotifica(clientInfo.id, clientInfo, {
                title: "¡Documentos aprobados! 🎉",
                body: `¡Excelente noticia! Todos los documentos de la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} han sido revisados y aprobados 📂. Ya puedes avanzar al siguiente paso del proceso. ¡Gracias!
				Fecha: ${fechaHoraEcuador}`,
                type: "success",
                empresa: "CREDI",
                url: "",
                tipo: "vendedor",
            });
            navigate("/ListadoSolicitud", { replace: true });
        } finally {
            setEnviandoAprobacion(false);
            setShowGlobalConfirmModal(false);
        }
    };

    // Aprobación dejando constancia de que faltan N respaldos por cargar.
    // El estado documental sigue quedando en "aprobado" (4); solo se registra el pendiente por separado.
    const handleAprobarConRespaldosFaltantes = async () => {
        if (detalleRespaldosFaltantes.trim().length < 10) {
            enqueueSnackbar("El detalle debe tener al menos 10 caracteres", { variant: "error" });
            return;
        }

        setEnviandoAprobacion(true);
        try {
            await updateEstadoVerificacion(4);
            if (Number(clientInfo?.TipoFirmaElectronica) === 2) {
                await axios.post(APIURL.autoAprobarBancoSolidario(clientInfo.id));
            }
            await crearRespaldosPendientes(cantidadRespaldosFaltantes, detalleRespaldosFaltantes.trim());

            fetchConsultaYNotifica(clientInfo.id, clientInfo, {
                title: "Documentos aprobados - Respaldos pendientes 📎",
                body: `¡Hola! La solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} fue aprobada, pero quedan ${cantidadRespaldosFaltantes} respaldo(s) pendiente(s) por cargar: ${detalleRespaldosFaltantes.trim()}. ¡Gracias!
				Fecha: ${fechaHoraEcuador}`,
                type: "success",
                empresa: "CREDI",
                url: "",
                tipo: "vendedor",
            });
            navigate("/ListadoSolicitud", { replace: true });
        } finally {
            setEnviandoAprobacion(false);
            setShowGlobalConfirmModal(false);
        }
    };

    // Función para manejar la acción global
    const handleGlobalConfirmAction = async () => {
        // Actualizar estado de todo el documento
        switch (globalConfirmAction) {
            case 'rechTodo':
                setShowGlobalConfirmModal(false);
                updateEstadoVerificacion(5);
                fetchConsultaYNotifica(clientInfo.id, clientInfo, {
                    title: "¡Documentos rechazados! 🚫",
                    body: `¡Hola! Todos los documentos de la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} fueron rechazados ☹️. Por favor, revisa los comentarios y da seguimiento al caso. ¡Gracias!
					Fecha: ${fechaHoraEcuador}`,
                    type: "success",
                    empresa: "CREDI",
                    url: "",
                    tipo: "vendedor",
                });
                navigate("/ListadoSolicitud", { replace: true });
                break;
            case 'aprobTodo':
                if (aprobarStep === 'faltanRespaldos') {
                    await handleAprobarConRespaldosFaltantes();
                } else {
                    await handleAprobarTodoCorrecto();
                }
                break;
            case 'revTodo':
                setShowGlobalConfirmModal(false);
                updateEstadoVerificacion(3);
                fetchConsultaYNotifica(clientInfo.id, clientInfo, {
                    title: "¡Documentos enviados a corrección! ✏️",
                    body: `¡Hola! Algunos documentos de la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con CI ${clientInfo.cedula} fueron enviados a corrección ⚠️. Revisa los comentarios para realizar los ajustes necesarios. ¡Gracias!
					Fecha: ${fechaHoraEcuador}`,
                    type: "success",
                    empresa: "CREDI",
                    url: "",
                    tipo: "vendedor",
                });
                navigate("/ListadoSolicitud", { replace: true });
                break;
            default:
                setShowGlobalConfirmModal(false);
                break;
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-100">
            <div
                className={`w-64 bg-[#2d3689] text-white ${isMenuOpen ? "block" : "hidden"} md:block transition-all duration-300 ease-in-out overflow-y-auto`}
            >
                <div className="p-6 fixed top-0 left-0 w-full h-full bg-white z-40 overflow-y-auto
  				transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:static md:translate-x-0 md:h-auto md:w-auto md:bg-transparent md:z-auto`}">
                    {clientInfo.estadoVerifD === 2 && (
                        <div>
                            <button name="rechTodo"
                                className="bg-red-500 text-white py-2 px-6 rounded-md shadow-lg hover:bg-red-700 transition duration-300 mb-3"
                                onClick={() => openGlobalConfirmModal('rechTodo')}
                            >
                                Rechazar
                            </button>
                            {showApproveAllButton && (
                                <button
                                    name="aprobTodo"
                                    className="bg-green-500 text-white py-2 px-6 rounded-md shadow-lg hover:bg-green-700 transition duration-300 mb-3"
                                    onClick={() => openGlobalConfirmModal('aprobTodo')}
                                >
                                    Aprobar
                                </button>
                            )}
                            {showRevisionButton && (
                                <button
                                    name="revTodo"
                                    className="bg-gray-500 text-white py-2 px-6 rounded-md shadow-lg hover:bg-gray-700 transition duration-300"
                                    onClick={() => openGlobalConfirmModal('revTodo')}
                                >
                                    Corrección
                                </button>
                            )}
                        </div>
                    )}
                    {/* Lista de todos los documentos */}
                    <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-2">Documentos:</h3>
                        {Object.entries(documentsByType).map(([typeName, docs]) => (
                            <div key={typeName} className="mb-4">
                                <h4
                                    className="text-white font-medium mb-1 cursor-pointer hover:underline"
                                    onClick={() => {
                                        const index = flatFiles.findIndex(file => file.sectionName === typeName);
                                        if (index !== -1) {
                                            setCurrentIndex(index);
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    {typeName}:
                                </h4>
                                <ul className="pl-3 space-y-1">
                                    {docs.map(doc => (
                                        <li
                                            key={doc.id}
                                            className={`text-sm flex items-center justify-between ${getStatusColor(doc.id)}`}
                                        >
                                            <span className="truncate">{doc.name}</span>
                                            <span className="text-xs ml-1 whitespace-nowrap">{getStatusText(doc.id)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        {/* Respaldos Pendientes: solo mientras el ciclo está en carga (Estado=1). Una vez
                            enviado a revisión, aprobado o negado, esta lista deja de habilitarse aquí. */}
                        {respaldosPendientesActivo?.Estado === RESPALDOS_PENDIENTE_CARGA && (
                            <div className="mb-4">
                                <h4 className="text-white font-medium mb-1">Respaldos Pendientes:</h4>
                                <ul className="pl-3 space-y-2">
                                    {Object.keys(RESPALDO_TIPOS)
                                        .filter((tipo) => !documentsByType[tipo])
                                        .map((tipo) => (
                                            <li key={tipo} className="text-sm">
                                                <div className="flex items-center justify-between text-amber-300">
                                                    <span className="truncate">{tipo}</span>
                                                    <span className="text-xs ml-1 whitespace-nowrap">• Pendiente</span>
                                                </div>
                                                <div className="flex items-center gap-1 mt-1">
                                                    <input
                                                        type="file"
                                                        onChange={(e) =>
                                                            setRespaldoArchivos((prev) => ({
                                                                ...prev,
                                                                [tipo]: e.target.files?.[0] || null,
                                                            }))
                                                        }
                                                        className="text-xs text-white w-full"
                                                        disabled={subiendoRespaldoTipo === tipo}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded shrink-0 disabled:opacity-50"
                                                        onClick={() => handleSubirRespaldo(tipo)}
                                                        disabled={subiendoRespaldoTipo === tipo || !respaldoArchivos[tipo]}
                                                    >
                                                        {subiendoRespaldoTipo === tipo ? "..." : "Subir"}
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                </ul>
                                <button
                                    type="button"
                                    className="w-full mt-2 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm rounded-md disabled:opacity-50"
                                    onClick={handleEnviarARevisar}
                                    disabled={resolviendoRespaldos}
                                >
                                    Enviar a revisión
                                </button>
                            </div>
                        )}
                        {/* Croquis tabs */}
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold mb-2">Croquis:</h3>
                            <button
                                className="w-full py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-700 transition mb-2"
                                onClick={() => {
                                    fetchCroquisCoords(1); // 1 = domicilio
                                    setIsMenuOpen(false);
                                }}
                            >
                                Croquis Domicilio
                            </button>
                            <button
                                className="w-full py-2 bg-green-500 text-white rounded-md shadow hover:bg-green-700 transition"
                                onClick={() => {
                                    fetchCroquisCoords(2); // 2 = laboral
                                    setIsMenuOpen(false);
                                }}
                            >
                                Croquis Laboral
                            </button>
                        </div>
                        {allDocuments.length === 0 && (
                            <p className="text-sm text-gray-300">No hay documentos disponibles</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Menu Toggle Button */}
            <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="fixed top-12 left-6 z-50 md:hidden bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition duration-300"
            >
                {isMenuOpen ? "❌" : "☰"}
            </button>

            {/* Main Content */}
            <div className="flex-1 p-3 md:p-6 bg-white">
                <div className="w-full bg-white p-4 md:p-6 rounded-lg shadow-lg">
                    <ClienteInfoCard clientInfo={clientInfo} formatDate={formatDate} navigate={navigate} permiso={puedeVisualizarfirma()} />

                    {respaldosPendientesActivo && respaldosPendientesActivo.Estado === RESPALDOS_PENDIENTE_CARGA && (
                        <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
                            <p className="text-sm font-semibold text-amber-800">
                                📎 Respaldos pendientes de carga ({respaldosPendientesActivo.CantidadRespaldos})
                            </p>
                            <p className="text-sm text-amber-700 mt-1">{respaldosPendientesActivo.Detalle}</p>
                            <p className="text-xs text-amber-600 mt-3 pt-3 border-t border-amber-200">
                                El vendedor/jefe de almacén debe cargar los archivos desde el panel "Respaldos Pendientes"
                                del menú lateral y luego enviarlos a revisión. Todavía no hay nada que aprobar.
                            </p>
                        </div>
                    )}

                    {respaldosPendientesActivo && respaldosPendientesActivo.Estado === RESPALDOS_ENVIADO_A_REVISAR && (
                        <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <p className="text-sm font-semibold text-amber-800">
                                    📎 Respaldos enviados a revisión ({respaldosPendientesActivo.CantidadRespaldos})
                                </p>
                                <p className="text-sm text-amber-700 mt-1">{respaldosPendientesActivo.Detalle}</p>
                            </div>
                            {puedeAprobarRespaldos() ? (
                                <div className="flex gap-2 shrink-0">
                                    {[RESPALDOS_VOLVER_A_CORREGIR, RESPALDOS_RECHAZADO, RESPALDOS_APROBADO].map((estado) => {
                                        const accion = RESPALDOS_ACCIONES[estado];
                                        const { Icon } = accion;
                                        return (
                                            <button
                                                key={estado}
                                                type="button"
                                                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-full shadow-sm hover:shadow-md active:scale-95 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none ${accion.buttonClasses}`}
                                                onClick={() => {
                                                    setDobleConfirmacionRespaldos(false);
                                                    setConfirmarAccionRespaldos(estado);
                                                }}
                                                disabled={resolviendoRespaldos}
                                            >
                                                <Icon size={16} />
                                                {accion.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-xs text-amber-600 italic shrink-0">
                                    Esperando revisión de un usuario con permiso para aprobar respaldos.
                                </p>
                            )}
                        </div>
                    )}

                    {confirmarAccionRespaldos && (() => {
                        const accion = RESPALDOS_ACCIONES[confirmarAccionRespaldos];
                        const { Icon } = accion;
                        return (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                                <div className="bg-white rounded-xl max-w-sm w-full shadow-2xl overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 shrink-0">
                                                <AlertTriangle className="w-5 h-5 text-amber-600" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-900">{accion.confirmTitle}</h3>
                                        </div>
                                        <p className="text-sm text-gray-600">{accion.confirmText}</p>

                                        <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                checked={dobleConfirmacionRespaldos}
                                                onChange={(e) => setDobleConfirmacionRespaldos(e.target.checked)}
                                                disabled={resolviendoRespaldos}
                                            />
                                            <span className="text-xs text-gray-600">{accion.confirmCheckLabel}</span>
                                        </label>
                                    </div>
                                    <div className="flex gap-2 px-6 pb-6">
                                        <button
                                            type="button"
                                            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                            onClick={() => setConfirmarAccionRespaldos(null)}
                                            disabled={resolviendoRespaldos}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 ${accion.confirmClasses}`}
                                            onClick={async () => {
                                                await handleResolverRespaldos(confirmarAccionRespaldos);
                                                setConfirmarAccionRespaldos(null);
                                            }}
                                            disabled={resolviendoRespaldos || !dobleConfirmacionRespaldos}
                                        >
                                            <Icon size={16} />
                                            {resolviendoRespaldos ? "Enviando..." : "Sí, confirmar"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    <div className="flex justify-center items-center mt-4 md:mt-6 w-full">
                        <h2 className={`text-xl md:text-2xl font-semibold text-center ${getCurrentDocumentStatusClass()}`}>
                            {getCurrentDocumentStatusTitle()}
                        </h2>
                    </div>

                    {/* Carrusel de documentos */}
                    <div className="mt-8 mb-6">
                        {flatFiles.length > 0 ? (
                            <div className="relative">
                                {/* Indicador de documento actual */}
                                <div className="text-center mb-2">
                                    <span className="text-sm font-semibold text-gray-700">
                                        Documento {currentIndex + 1} de {flatFiles.length} - {flatFiles[currentIndex].sectionName}
                                    </span>
                                    <span className={`ml-2 text-sm font-medium ${flatFiles[currentIndex].estado === 3 ? "text-green-600" :
                                        flatFiles[currentIndex].estado === 4 ? "text-red-600" :
                                            "text-gray-500"}`}>
                                        {flatFiles[currentIndex].estado === 3 ? "(Aprobado)" :
                                            flatFiles[currentIndex].estado === 4 ? "(Rechazado)" :
                                                "(Pendiente)"}
                                    </span>
                                </div>

                                {/* Contenedor carrusel */}
                                <div className="flex items-center">
                                    {/* Botón Anterior */}
                                    <button
                                        onClick={prevSlide}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-full shadow-md mr-4 focus:outline-none"
                                        aria-label="Documento anterior"
                                    >
                                        <ArrowBackIosIcon fontSize="small" />
                                    </button>

                                    {/* Documento actual */}
                                    <div className={`flex-1 bg-gray-50 p-6 rounded-lg shadow-lg border ${flatFiles[currentIndex].estado === 3 ? "border-green-300" :
                                        flatFiles[currentIndex].estado === 4 ? "border-red-300" :
                                            "border-gray-200"} relative md:h-screen flex flex-col`}>

                                        {/* Estado visual del documento */}
                                        {flatFiles[currentIndex].estado === 3 && (
                                            <div className="absolute top-0 left-0 w-full bg-green-100 text-green-800 py-1 px-4 text-center font-medium">
                                                ✅ Este documento esta aprobado ✅
                                            </div>
                                        )}
                                        {flatFiles[currentIndex].estado === 4 && (
                                            <div className="absolute top-0 left-0 w-full bg-red-100 text-red-800 py-1 px-4 text-center font-medium">
                                                ❌ Este documento esta rechazado ❌
                                            </div>
                                        )}

                                        {/* Botones */}
                                        <div className="absolute top-2 right-2 flex items-center gap-3 z-10 bg-grey-50 bg-opacity-70 p-3 rounded-md">
                                            {renderActionButtons(flatFiles[currentIndex])}
                                        </div>

                                        {/* Información del documento */}
                                        <div className="mb-4 mt-8">
                                            <h3 className="text-lg font-semibold text-gray-800">
                                                {flatFiles[currentIndex].sectionName}
                                            </h3>
                                            <p className="text-sm text-gray-500">{flatFiles[currentIndex].name}</p>
                                        </div>

                                        {/* Vista previa del documento */}
                                        {flatFiles[currentIndex].sectionName === "Croquis" ? (
                                            // No mostrar nada, el modal ya se abrirá por el menú lateral
                                            <div className="w-full flex-1 flex items-center justify-center">
                                                <p className="text-gray-400">Selecciona otro documento o cierra el mapa.</p>
                                            </div>
                                        ) : (
                                            <div className="w-full flex-1 flex items-center justify-center">
                                                <object
                                                    data={flatFiles[currentIndex].url}
                                                    type="application/pdf"
                                                    className="w-full h-full rounded-md"
                                                    aria-label="Vista previa PDF"
                                                >
                                                    <p>Vista previa no disponible</p>
                                                </object>
                                            </div>
                                        )}
                                    </div>

                                    {/* Botón Siguiente */}
                                    <button
                                        onClick={nextSlide}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-full shadow-md ml-4 focus:outline-none"
                                        aria-label="Documento siguiente"
                                    >
                                        <ArrowForwardIosIcon fontSize="small" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-gray-500">No hay documentos pendientes para esta solicitud.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Modal de confirmación */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div
                        ref={confirmModalRef}
                        className="bg-white p-6 rounded-lg shadow-lg w-1/3 max-w-md relative"
                    >
                        <div className="mb-4">
                            <h3 className="text-xl font-medium text-gray-900">
                                {confirmAction === 'aprobar' ? 'Aprobar Documento' : 'Rechazar Documento'}
                            </h3>
                            <p className="text-sm text-gray-500 mt-2">
                                {confirmAction === 'aprobar'
                                    ? ''
                                    : 'Seleccione el motivo del rechazo.'}
                            </p>
                        </div>

                        <div className="mb-4">
                            {confirmAction === 'rechazar' && (<label htmlFor="observacion" className="block text-sm font-medium text-gray-700 mb-1">
                                Observación {confirmAction === 'rechazar' && <span className="text-red-500">*</span>}
                            </label>)}
                            {confirmAction === 'rechazar' && (<select id="observacion" value={observacion} onChange={(e) => setObservacion(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500">
                                <option value="">Seleccione un motivo</option>
                                <option value="Firmas Inconformes">Firmas Inconformes</option>
                                <option value="Documento Errado">Documento Errado</option>
                                <option value="Documento Sin Firmas">Documento sin Firmas</option>
                                <option value="Documento con Datos Incorrectos">Documento con Datos Incorrectos</option>
                            </select>)}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                onClick={handleCancelAction}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 text-white rounded-md ${confirmAction === 'aprobar'
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                onClick={handleConfirmAction}
                            >
                                {confirmAction === 'aprobar' ? 'Aprobar' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {showMapModal && mapLat && mapLng && (
                <GoogleMapModal
                    lat={mapLat}
                    lng={mapLng}
                    title={mapTitle}
                    apiKey={GOOGLE_MAPS_API_KEY}
                    onClose={() => setShowMapModal(false)}
                />
            )}
            {/* Modal global */}
            {showGlobalConfirmModal && (
                globalConfirmAction === 'rechTodo' ? (
                    <ModalConfirmacionRechazo
                        isOpen={true}
                        onClose={() => setShowGlobalConfirmModal(false)}
                        onConfirm={(observacionModal) => {
                            setShowGlobalConfirmModal(false);

                            // Aquí va la lógica de rechazo global:
                            updateEstadoVerificacionrechazo(5, observacionModal);

                            fetchConsultaYNotifica(clientInfo.id, clientInfo, {
                                title: "¡Documentos rechazados! 🚫",
                                body: `¡Hola! Todos los documentos de la solicitud ${clientInfo.NumeroSolicitud} de ${clientInfo.nombre} con con CI ${clientInfo.cedula} fueron rechazados ☹️. Por favor, revisa los comentarios y da seguimiento al caso. ¡Gracias!
                    Fecha: ${fechaHoraEcuador}`,
                                type: "success",
                                empresa: "CREDI",
                                url: "",
                                tipo: "vendedor",
                            });
                            navigate("/ListadoSolicitud", { replace: true });
                        }}
                        solicitudData={clientInfo}
                        mensajePrincipal="¿Está seguro de que desea rechazar todos los documentos?"
                    />
                ) : globalConfirmAction === 'aprobTodo' && aprobarStep === 'faltanRespaldos' ? (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                        <div
                            ref={globalConfirmModalRef}
                            className="bg-white p-6 rounded-lg shadow-lg w-1/3 max-w-md relative"
                        >
                            <div className="mb-4">
                                <h3 className="text-xl font-medium text-gray-900">Respaldos Pendientes</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    La solicitud quedará aprobada; se registrará que faltan respaldos por cargar.
                                </p>
                            </div>

                            <div className="mb-4">
                                <label htmlFor="cantidadRespaldos" className="block text-sm font-medium text-gray-700 mb-1">
                                    ¿Cuántos respaldos hacen falta? <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="cantidadRespaldos"
                                    value={cantidadRespaldosFaltantes}
                                    onChange={(e) => setCantidadRespaldosFaltantes(Number(e.target.value))}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-4">
                                <label htmlFor="detalleRespaldos" className="block text-sm font-medium text-gray-700 mb-1">
                                    Detalle <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    id="detalleRespaldos"
                                    value={detalleRespaldosFaltantes}
                                    onChange={(e) => setDetalleRespaldosFaltantes(e.target.value)}
                                    placeholder="Indique qué respaldo(s) falta(n) y por qué (mínimo 10 caracteres)"
                                    rows="3"
                                    maxLength={350}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-xs text-gray-400">{detalleRespaldosFaltantes.length}/350</span>
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                    onClick={() => setAprobarStep('choice')}
                                    disabled={enviandoAprobacion}
                                >
                                    Atrás
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 text-white rounded-md bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                    onClick={handleGlobalConfirmAction}
                                    disabled={enviandoAprobacion || detalleRespaldosFaltantes.trim().length < 10}
                                >
                                    {enviandoAprobacion ? 'Enviando...' : 'Confirmar'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                        <div
                            ref={globalConfirmModalRef}
                            className="bg-white p-6 rounded-lg shadow-lg w-1/3 max-w-md relative"
                        >
                            <div className="mb-4">
                                <h3 className="text-xl font-medium text-gray-900">
                                    {globalConfirmAction === 'aprobTodo'
                                        ? 'Aprobar Todos los Documentos'
                                        : 'Enviar Todos los Documentos a Corrección'}
                                </h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    {globalConfirmAction === 'aprobTodo'
                                        ? '¿Todo está correcto o falta algún documento de respaldo?'
                                        : '¿Está seguro de que desea enviar todos los documentos a corrección?'}
                                </p>
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                    onClick={() => setShowGlobalConfirmModal(false)}
                                    disabled={enviandoAprobacion}
                                >
                                    Cancelar
                                </button>
                                {globalConfirmAction === 'aprobTodo' && (
                                    <button
                                        type="button"
                                        className="px-4 py-2 text-white rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50"
                                        onClick={() => setAprobarStep('faltanRespaldos')}
                                        disabled={enviandoAprobacion}
                                    >
                                        Faltan respaldos
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${globalConfirmAction === 'aprobTodo'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-gray-600 hover:bg-gray-700'
                                        }`}
                                    onClick={handleGlobalConfirmAction}
                                    disabled={enviandoAprobacion}
                                >
                                    {enviandoAprobacion
                                        ? 'Enviando...'
                                        : globalConfirmAction === 'aprobTodo'
                                            ? 'Todo correcto'
                                            : 'Corrección'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}
        </div>
    );
}